import { Component } from '@angular/core';

import { User } from '@app/_models';
import { AccountService } from '@app/_services';
import { ReportsService } from '@app/_services/reports.service';
import { Subscription } from 'rxjs';

@Component({ templateUrl: 'home.component.html', styleUrls: ['home.component.css'] })
export class HomeComponent {
    user: User | null;
    dayWiseClosing: any[] = [];
    isLoading: boolean = false;
    
    // Dashboard Stats - Static (don't change with date)
    staticStats: any = {
        stockValue: 0,
        stockCostValue: 0,
        stockQuantity: 0,
        totalProducts: 0,
        outstandingAmount: 0
    };
    isLoadingStaticStats: boolean = false;
    
    // Dashboard Stats - Date-filtered
    dateFilteredStats: any = {
        totalSales: 0,
        totalItemQty: 0,
        totalExchanges: 0,
        totalPurchaseQty: 0
    };
    isLoadingDateStats: boolean = false;
    transporterStats = {
        receiptCount: 0,
        totalWeight: 0,
        totalFreight: 0,
        paymentCount: 0,
        totalPaymentAmount: 0
    };
    isLoadingTransporterStats: boolean = false;
    
    // Date Range Selection
    selectedDateRange: string = 'today';
    customStartDate: string = '';
    customEndDate: string = '';
    dateRangeOptions = [
        { value: 'today', label: 'Today' },
        { value: 'yesterday', label: 'Yesterday' },
        { value: 'thisWeek', label: 'This Week' },
        { value: 'thisMonth', label: 'This Month' },
        { value: 'previousMonth', label: 'Previous Month' },
        { value: 'last15Days', label: 'Last 15 Days' },
        { value: 'last30Days', label: 'Last 30 Days' },
        { value: 'thisQuarter', label: 'This Quarter' },
        { value: 'lastQuarter', label: 'Last Quarter' },
        { value: 'thisFinancialYear', label: 'This Financial Year' },
        { value: 'lastFinancialYear', label: 'Last Financial Year' },
        { value: 'custom', label: 'Custom Date Range' }
    ];
    
    // Pagination properties
    currentPage: number = 1;
    pageSize: number = 10;
    totalRecords: number = 0;
    private dashboardRequests = new Subscription();
    private selectedCompanyId = '';
    private selectedBranchId = '';
    
    // Make Math available in template
    Math = Math;
    
    constructor(public accountService: AccountService,
        private reportsService: ReportsService
    ) {
        this.user = this.accountService.userValue;
    }
    
    ngOnInit() {
        if (this.accountService.isAdmin()) return;
        this.refreshDashboard();
    }

    ngOnDestroy() {
        this.dashboardRequests.unsubscribe();
    }

    onSelectionReady(selection: { companyId: string; branchId: string }) {
        this.selectedCompanyId = selection.companyId;
        this.selectedBranchId = selection.branchId;
        this.refreshDashboard();
    }

    private refreshDashboard() {
        this.dashboardRequests.unsubscribe();
        this.dashboardRequests = new Subscription();
        if (this.accountService.isAdmin() && !this.accountService.getCurrentCompanyBranchSelection().companyId) {
            this.staticStats = { stockValue: 0, stockCostValue: 0, stockQuantity: 0, totalProducts: 0, outstandingAmount: 0 };
            this.dateFilteredStats = { totalSales: 0, totalItemQty: 0, totalExchanges: 0, totalPurchaseQty: 0 };
            this.transporterStats = { receiptCount: 0, totalWeight: 0, totalFreight: 0, paymentCount: 0, totalPaymentAmount: 0 };
            this.dayWiseClosing = [];
            this.totalRecords = 0;
            this.isLoading = this.isLoadingStaticStats = this.isLoadingDateStats = this.isLoadingTransporterStats = false;
            return;
        }
        if (this.accountService.isTransporter()) {
            this.fetchTransporterStats();
        } else {
            this.fetchStaticStats();
            this.fetchDateFilteredStats();
        }
        this.fetchDayWiseClosing();
    }
    
    // Company and Branch selection handlers - Only for Admin users
    onCompanyChange(companyId: string) {
        this.selectedCompanyId = companyId;
        this.selectedBranchId = '';
        this.accountService.updateCompanyBranchSelection(companyId, '');
        console.log('Selected Company ID:', companyId);
        this.refreshDashboard();
    }

    onBranchChange(branchId: string) {
        if (this.selectedBranchId === branchId) return;
        this.selectedBranchId = branchId;
        this.accountService.updateCompanyBranchSelection(this.selectedCompanyId, branchId);
        console.log('Selected Branch ID:', branchId);
        this.refreshDashboard();
    }
    
    onDateRangeChange() {
        if (this.accountService.isTransporter()) {
            this.fetchTransporterStats();
        } else {
            this.fetchDateFilteredStats();
        }
    }
    
    getDateRange(): { startDate: string, endDate: string } {
        const today = new Date();
        let startDate: Date;
        let endDate: Date = new Date(today);
        
        switch (this.selectedDateRange) {
            case 'today':
                startDate = new Date(today);
                break;
            case 'yesterday':
                startDate = new Date(today);
                startDate.setDate(startDate.getDate() - 1);
                endDate = new Date(startDate);
                break;
            case 'thisWeek':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - today.getDay());
                break;
            case 'thisMonth':
                startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                break;
            case 'previousMonth':
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case 'last15Days':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 14);
                break;
            case 'last30Days':
                startDate = new Date(today);
                startDate.setDate(today.getDate() - 29);
                break;
            case 'thisQuarter':
                const currentQuarter = Math.floor(today.getMonth() / 3);
                startDate = new Date(today.getFullYear(), currentQuarter * 3, 1);
                break;
            case 'lastQuarter':
                const lastQuarter = Math.floor(today.getMonth() / 3) - 1;
                const lastQuarterYear = lastQuarter < 0 ? today.getFullYear() - 1 : today.getFullYear();
                const adjustedQuarter = lastQuarter < 0 ? 3 : lastQuarter;
                startDate = new Date(lastQuarterYear, adjustedQuarter * 3, 1);
                endDate = new Date(lastQuarterYear, adjustedQuarter * 3 + 3, 0);
                break;
            case 'thisFinancialYear':
                // Financial year starts April 1
                const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
                startDate = new Date(fyStartYear, 3, 1);
                break;
            case 'lastFinancialYear':
                const lastFyStartYear = today.getMonth() >= 3 ? today.getFullYear() - 1 : today.getFullYear() - 2;
                startDate = new Date(lastFyStartYear, 3, 1);
                endDate = new Date(lastFyStartYear + 1, 2, 31);
                break;
            case 'custom':
                // Use custom dates if provided, otherwise use today
                if (this.customStartDate && this.customEndDate) {
                    return {
                        startDate: this.customStartDate,
                        endDate: this.customEndDate
                    };
                }
                startDate = new Date(today);
                break;
            default:
                startDate = new Date(today);
        }
        
        // Format dates without timezone conversion to avoid date shifting
        const formatDate = (date: Date): string => {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        };
        
        return {
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        };
    }
    
    fetchStaticStats() {
        this.isLoadingStaticStats = true;
        
        const selection = this.accountService.getCurrentCompanyBranchSelection();
        if (this.accountService.isAdmin() && !selection.companyId) {
            this.isLoadingStaticStats = false;
            return;
        }
        
        this.dashboardRequests.add(this.reportsService.getDashboardStats(selection.companyId, selection.branchId).subscribe(
            (response: any) => {
                this.staticStats = {
                    stockValue: response.stockValue || 0,
                    stockCostValue: response.stockCostValue || 0,
                    stockQuantity: response.stockQuantity || 0,
                    totalProducts: response.totalProducts || 0,
                    outstandingAmount: response.outstandingAmount || 0
                };
                this.isLoadingStaticStats = false;
            },
            (error) => {
                console.error('Error fetching static stats:', error);
                this.isLoadingStaticStats = false;
            }
        ));
    }
    
    fetchDateFilteredStats() {
        this.isLoadingDateStats = true;
        
        const selection = this.accountService.getCurrentCompanyBranchSelection();
        if (this.accountService.isAdmin() && !selection.companyId) {
            this.isLoadingDateStats = false;
            return;
        }
        const { startDate, endDate } = this.getDateRange();
        
        this.dashboardRequests.add(this.reportsService.getDashboardStatsByDate(startDate, endDate, selection.companyId, selection.branchId).subscribe(
            (response: any) => {
                this.dateFilteredStats = {
                    totalSales: response.totalSales || 0,
                    totalItemQty: response.totalItemQty || 0,
                    totalExchanges: response.totalExchanges || 0,
                    totalPurchaseQty: response.totalPurchaseQty || 0
                };
                this.isLoadingDateStats = false;
            },
            (error) => {
                console.error('Error fetching date-filtered stats:', error);
                this.isLoadingDateStats = false;
            }
        ));
    }

    fetchTransporterStats() {
        this.isLoadingTransporterStats = true;

        const selection = this.accountService.getCurrentCompanyBranchSelection();
        if (this.accountService.isAdmin() && !selection.companyId) {
            this.isLoadingTransporterStats = false;
            return;
        }
        const { startDate, endDate } = this.getDateRange();

        this.dashboardRequests.add(this.reportsService.getTransporterDashboardStatsByDate(startDate, endDate, selection.companyId, selection.branchId).subscribe(
            (response: any) => {
                this.transporterStats = {
                    receiptCount: response.receiptCount || 0,
                    totalWeight: response.totalWeight || 0,
                    totalFreight: response.totalFreight || 0,
                    paymentCount: response.paymentCount || 0,
                    totalPaymentAmount: response.totalPaymentAmount || 0
                };
                this.isLoadingTransporterStats = false;
            },
            (error) => {
                console.error('Error fetching transporter stats:', error);
                this.isLoadingTransporterStats = false;
            }
        ));
    }
    
    fetchDayWiseClosing() {
        this.isLoading = true;
        
        // Get current company/branch selection from AccountService
        const selection = this.accountService.getCurrentCompanyBranchSelection();
        if (this.accountService.isAdmin() && !selection.companyId) {
            this.isLoading = false;
            return;
        }
        
        this.dashboardRequests.add(this.reportsService.getDayWiseClosing(this.currentPage, this.pageSize, selection.companyId, selection.branchId).subscribe(
            (response: any) => {
                // Handle paginated response structure
                const data = response.data || response.items || response;
                this.totalRecords = response.totalRecords || response.totalCount || data.length;
                
                // Backend should handle all calculations, frontend just displays
                this.dayWiseClosing = data;
                
                this.isLoading = false;
            },
            (error) => {
                console.error('Error fetching day-wise closing:', error);
                this.isLoading = false;
            }
        ));
    }
    
    // Handle page changes
    onPageChange(page: number) {
        this.currentPage = page;
        this.fetchDayWiseClosing();
    }
    
    get totalPages(): number {
        return Math.ceil(this.totalRecords / this.pageSize);
    }

}